import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert } from '../../components/ui/alert'; // Assuming Alert is from shadcn/ui
import { Box, Stack, Title as MantineTitle, Text, Group } from '@mantine/core';
import { IconMapping } from '../../data';
import {
    PROPERTY_AREA_SQM,
    // historicalPriceData, // No longer needed directly for currentMonthlyRent
    // initialAgentPriceDefault // No longer needed, comes from props
} from '../../data'; // Import necessary data

// const TAX_RATE = 0.5; // Removed, will come from props

interface RecommendationPanelProps {
    agentRecommendedPrice: number; // Full amount, not in millions
    currentMonthlyRent: number;
    taxRate: number;
}

const RecommendationPanel: React.FC<RecommendationPanelProps> = ({
    agentRecommendedPrice, // This is the full amount, e.g., 1300000
    currentMonthlyRent,
    taxRate
}) => {
    // --- Data Calculations ---
    const propertyArea = PROPERTY_AREA_SQM; // 37.5

    // Agent's recommended price (from props)
    const agentRecommendedSalePrice = agentRecommendedPrice; // Use prop directly
    const agentRecommendedSalePriceMillion = agentRecommendedSalePrice / 1000000;

    // Current monthly rent (from props)
    // const currentMonthlyRent is now a prop

    // Market Valuation - this remains a fixed calculation based on a specific price/sqm for this panel's context
    const commercialPricePerSqmForValuation = 37200; 
    const marketValuation = propertyArea * commercialPricePerSqmForValuation; // 37.5 * 37200 = 1,395,000
    const marketValuationMillion = marketValuation / 1000000; // 1.395

    // Rental ROI based on agent's recommended price and current rent (from props)
    const annualRent = currentMonthlyRent * 12;
    const currentRentalROI = agentRecommendedSalePrice > 0 ? (annualRent / agentRecommendedSalePrice) * 100 : 0;

    // Calculations considering tax (taxRate from props)
    const netProceedsAfterTax = agentRecommendedSalePrice * (1 - taxRate);
    const netProceedsAfterTaxMillion = netProceedsAfterTax / 1000000;
    const yieldOnNetProceeds = netProceedsAfterTax > 0 ? (annualRent / netProceedsAfterTax) * 100 : 0;

    // --- End Data Calculations ---

    return (
        <Stack gap="lg">
            <Card>
                <CardHeader><CardTitle><Group gap="xs"><IconMapping.Info size={20}/>投資建議與深度分析</Group></CardTitle></CardHeader>
                <CardContent pt="md">
                    <Stack gap="xl">
                        <Box>
                            <MantineTitle order={3} mb="md">引言與市場概覽</MantineTitle>
                            <Text mb="sm">
                                本分析基於對廣州市荔灣區芳村商業地產市場過去15年價格走勢的深入研究，以及對該 
                                {propertyArea}平方米小區內商舖目前投資價值的綜合評估。該商舖的市場估值約為 
                                {marketValuationMillion.toFixed(3)}百萬元，目前月租金 
                                {currentMonthlyRent.toLocaleString()}元處於市場合理水平。
                            </Text>
                            <Text>芳村區域商業地產市場在經歷了2010年至2019年的快速增長後，目前正處於調整期。受電商衝擊、消費習慣改變以及新興商圈競爭等多重因素影響，小區內商舖的投資價值相對下降。展望未來，市場可能繼續承壓，商舖價格恐難實現明顯上漲，租金水平雖相對穩定，但增長空間有限。</Text>
                        </Box>

                        <Box>
                            <MantineTitle order={3} mb="md">核心權衡：出售 vs. 出租</MantineTitle>

                            <MantineTitle order={4} mb="sm" mt="md">選項一：出售商舖</MantineTitle>
                            <Text><strong>核心思路：</strong>建議業主考慮在適當時機出售該商舖，以規避未來可能的市場風險。</Text>
                            <MantineTitle order={5} my="xs">支持理由 (優勢)</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li>規避市場下行風險：鑒於市場前景不明朗，價格上漲空間有限，出售可鎖定目前價值。</li>
                                <li>
                                    資金釋放：以中介推薦價 {agentRecommendedSalePriceMillion.toFixed(1)}百萬元 出售，扣除約 {(taxRate * 100).toFixed(0)}% 的稅費後，預計可得淨資金約 {netProceedsAfterTaxMillion.toFixed(2)}百萬元，可用於其他投資。
                                </li>
                                <li>
                                    價格合理性：中介推薦的 {agentRecommendedSalePriceMillion.toFixed(1)}百萬元售價雖略低於市場估值 {marketValuationMillion.toFixed(3)}百萬元，
                                    但在目前交易不活躍及潛在下行壓力下，仍屬合理範圍。
                                </li>
                            </Text>
                            <MantineTitle order={5} my="xs">潛在挑戰 (考量)</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li>高額稅負：按 {(taxRate * 100).toFixed(0)}% 估算的稅費將大幅減少實際到手金額，構成巨大的「退出成本」。</li>
                                <li>售價可能略低於期望：實際成交價可能無法完全達到最高估值。</li>
                                <li>交易週期：市場不活躍可能導致銷售週期較長。</li>
                            </Text>
                            <MantineTitle order={5} my="xs">建議策略</MantineTitle>
                            <Text>
                                掛牌價格建議在 {agentRecommendedSalePriceMillion.toFixed(1)}至{marketValuationMillion.toFixed(1)}百萬元區間，並根據市場反饋靈活調整。
                                可考慮多家實力中介聯合代理以擴大曝光。適當修繕商舖，提升賣相。出售期間可繼續出租以維持現金流。建議爭取在未來6個月內完成交易，以應對潛在價格壓力。密切關注區域政策及市場動態，捕捉有利時機。
                            </Text>

                            <MantineTitle order={4} mb="sm" mt="lg">選項二：繼續出租</MantineTitle>
                            <Text><strong>核心思路：</strong>若短期內無法以理想價格出售，可繼續出租作為過渡，但需積極優化租金回報。</Text>
                            <MantineTitle order={5} my="xs">適用情況與優勢</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li>維持現金流：在尋求出售的過程中，租金可以提供持續的現金收入。</li>
                                <li>
                                    提升回報潛力：租約到期後，可嘗試將月租金提升至2,300-2,500元，以改善租金回報率。
                                    {/* This target range 2,300-2,500 is kept from original panel text as an aspirational/strategic goal */}
                                    目前年租金 {annualRent.toLocaleString()}元，相對於稅後可回收資金 {netProceedsAfterTaxMillion.toFixed(2)}百萬元 而言，實際機會成本回報率約為 {yieldOnNetProceeds.toFixed(2)}%。
                                </li>
                            </Text>
                            <MantineTitle order={5} my="xs">潛在挑戰與回報率分析</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li>
                                    傳統租金回報率偏低：目前基於中介建議售價 {agentRecommendedSalePriceMillion.toFixed(1)}百萬元 計算的傳統租金回報率僅為 {currentRentalROI.toFixed(2)}%，
                                    這可能低於多數銀行存款利率。若考慮到出售需支付 {(taxRate * 100).toFixed(0)}% 的稅款，稅後實際可回收資金約 {netProceedsAfterTaxMillion.toFixed(2)}百萬元，
                                    基於此金額的年租金回報（機會成本回報率）為 {yieldOnNetProceeds.toFixed(2)}%。業主需判斷此回報率是否具吸引力。
                                </li>
                                <li>資產增值空間有限：商舖主要價值體現在租金收益，未來資產大幅增值的可能性較低。</li>
                                <li>租金增長受限：整體市場環境限制了租金大幅上漲的空間。</li>
                            </Text>
                            <MantineTitle order={5} my="xs">建議策略</MantineTitle>
                            <Text>繼續出租時，應致力於尋找更優質、穩定的租戶（如連鎖品牌或有良好經營歷史的商戶）。可考慮進行適度投入以改善商舖條件，從而支持租金提升和提高出租率，但務必控制成本以確保投資回報。需明確，從長遠角度看，若市場條件允許，仍建議出售該商舖。</Text>
                        </Box>

                        <Box>
                            <MantineTitle order={3} mb="md">綜合投資建議與主要風險</MantineTitle>
                            <MantineTitle order={4} mb="sm">核心建議</MantineTitle>
                            <Text mb="sm">綜合考量目前市場狀況、未來趨勢以及高達 {(taxRate * 100).toFixed(0)}% 的潛在出售稅負，我們<strong>建議業主謹慎評估出售與繼續持有的利弊</strong>。
                                出售可規避市場風險並釋放資金（稅後約 {netProceedsAfterTaxMillion.toFixed(2)}百萬元），但需承受巨額稅損。
                                繼續持有則以獲取相對於稅後可回收資金的 {yieldOnNetProceeds.toFixed(2)}% 機會成本回報為目標，同時承擔資產貶值和管理風險。
                                若市場持續下行壓力大或有更優投資渠道，優先考慮出售；反之，若短期難以理想價格（稅後）出售，繼續持有並優化租金不失為一種策略。
                            </Text>
                            
                            <MantineTitle order={4} mb="sm" mt="lg">主要風險考量</MantineTitle>
                            <Text mb="xs">無論最終選擇如何，商業地產投資均伴隨固有風險。以下為針對不同決策路徑的風險細分：</Text>

                            <MantineTitle order={5} my="xs">1. 共同面臨的核心風險：</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li><strong>市場下行風險：</strong>宏觀經濟波動、區域發展不及預期或商業地產整體供需失衡，均可能導致商舖價值縮水或租金水平下降。</li>
                                <li><strong>政策調整風險：</strong>國家或地方關於商業地產的稅收、貸款、規劃用途、租賃管理等政策調整，可能對投資回報和資產價值產生直接或間接影響。</li>
                                <li><strong>經濟環境變化風險：</strong>整體經濟增長放緩、消費者信心不足、特定行業蕭條等，將直接影響商舖的經營環境和盈利能力。</li>
                            </Text>

                            <MantineTitle order={5} my="xs" mt="md">2. 選擇「出售商舖」的主要考量與潛在風險：</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li><strong>巨額稅負成本：</strong>高達 {(taxRate * 100).toFixed(0)}% 的預估稅費將顯著侵蝕出售所得，是決策中的關鍵考量因素。</li>
                                <li><strong>市場時機與定價風險：</strong>在目前市場環境下，可能难以在短期內以完全符合期望的最高估值 {marketValuationMillion.toFixed(3)}百萬元 成交，存在一定議價空間和時間成本。</li>
                                <li><strong>交易週期不確定性：</strong>商業地產交易流程相對複雜，市場活躍度不高可能導致銷售週期延長。</li>
                            </Text>

                            <MantineTitle order={5} my="xs" mt="md">3. 選擇「繼續出租」的主要考量与潛在風險：</MantineTitle>
                            <Text component="ul" style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li><strong>機會成本回報率：</strong>相對於稅後可回收資金 {netProceedsAfterTaxMillion.toFixed(2)}百萬元，目前的年租金 {annualRent.toLocaleString()}元 產生的機會成本回報率為 {yieldOnNetProceeds.toFixed(2)}%。此回報率需與其他投資機會比較。傳統基於購入價或市場估價的回報率 ({currentRentalROI.toFixed(2)}%) 可能掩蓋了因高稅負導致的實際資金運用效益問題。</li>
                                <li><strong>租戶流失與空置風險：</strong>租約到期後可能面臨招租困難或空置期，經濟不景氣或周邊競爭加劇會放大此風險。</li>
                                <li><strong>管理成本與精力投入：</strong>物業維護、租戶溝通、賬務處理等日常管理需要持續投入時間和精力。</li>
                                <li><strong>資產流動性受限：</strong>相較於直接出售，持有出租物業意味著資金繼續沉澱在該資產中，未來若需快速變現可能面臨挑戰。</li>
                            </Text>
                            
                            <Text mt="md"><strong>風險應對建議：</strong>建議業主密切關注宏觀經濟走向、區域商業發展動態及相關政策變化。定期（如每季度或每半年）審視資產表現與市場狀況，靈活調整經營或出售策略，以主動管理和緩釋上述風險。</Text>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
            <Alert title="免責聲明" icon={<IconMapping.AlertCircle size={16}/>} color="yellow" variant="filled">
                <Text>本報告基於公開市場數據和專業分析製作，僅供參考，不構成任何投資建議。市場有風險，投資需謹慎。任何投資決策應由投資者自行承擔責任。報告中的數據和分析可能受到多種因素影響而發生變化，我們不對因使用本報告而導致的任何損失承擔責任。特別提示，關於稅費的計算基於通用假設（{(taxRate * 100).toFixed(0)}%），實際稅務處理請諮詢專業稅務顧問。</Text>
            </Alert>
        </Stack>
    );
}

export default RecommendationPanel; 